import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useI18n } from "../../../i18n";
import { api } from "../../../lib/api";
import { InviteForm } from "./InviteForm";
import { useAuth } from "../../../context/AuthContext";
import { hasPerm } from "../../../lib/perm";
import { UsersTable } from "./UsersTable";
import { StaffUsers } from "./StaffUsers";
import { Outbox } from "./Outbox";

const copy = (text, t) => navigator.clipboard?.writeText(text).then(() => toast.success(t("users.copied"))).catch(() => {});

export default function UsersAdmin() {
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [mails, setMails] = useState([]);
  const [lastLink, setLastLink] = useState("");
  const [tab, setTab] = useState("customers");

  const { user } = useAuth();
  const power = hasPerm(user, "users:manage");
  const load = useCallback(() => {
    api.get("/admin/users", { params: { type: "customer" } }).then((r) => setUsers(r.data)).catch(() => toast.error(t("common.error")));
    if (power) api.get("/admin/users/outbox").then((r) => setMails(r.data)).catch(() => {});
  }, [t, power]);

  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, [load]);

  const byCode = useMemo(() => Object.fromEntries(customers.map((c) => [c.code, c])), [customers]);
  const onLink = (link) => { setLastLink(link); load(); };

  return (
    <div className="space-y-6" data-testid="users-admin-page">
      <div>
        <h1 className="text-3xl font-bold text-navy sm:text-4xl">{t("users.title")}</h1>
        <p className="mt-2 text-sm text-slate-500">{t("users.subtitle")}</p>
      </div>
      <div className="flex gap-2">
        {(power ? ["customers", "staff"] : ["customers"]).map((k) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`users-tab-${k}`} className={`h-10 rounded-full px-4 text-sm font-semibold ${tab === k ? "bg-navy text-white" : "bg-white text-navy shadow-sm"}`}>{t(`ops.${k}Tab`)}</button>
        ))}
      </div>
      {tab === "customers" && <InviteForm customers={customers} onInvited={onLink} />}
      {lastLink && (
        <div className="flex items-center gap-3 rounded-2xl border border-brand/30 bg-brand/10 px-4 py-3 text-sm" data-testid="invite-link-box">
          <span className="min-w-0 flex-1 truncate text-navy" data-testid="invite-link-value">{lastLink}</span>
          <button type="button" onClick={() => copy(lastLink, t)} className="h-11 rounded-full bg-navy px-4 text-xs font-semibold text-white" data-testid="invite-link-copy">{t("users.copy")}</button>
        </div>
      )}
      {tab === "customers" ? <UsersTable users={users} customers={customers} customersByCode={byCode} onChanged={load} onLink={onLink} /> : <StaffUsers onLink={onLink} />}
      {power && <Outbox mails={mails} onSent={load} />}
    </div>
  );
}
